import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Alert
} from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';
import { apiUrl, getPublicJsonHeaders, API_BASE_URL, apiFetch, setStoredTokens } from '../config/api';

const LoginForm = ({ onLogin }) => {
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await apiFetch(apiUrl('/auth/login'), {
                method: 'POST',
                headers: getPublicJsonHeaders(),
                body: JSON.stringify({
                    email: formData.email,
                    password: formData.password
                }),
            });

            const text = await response.text();
            let data = {};
            try {
                data = text ? JSON.parse(text) : {};
            } catch {
                const looksHtml = /^\s*</.test(text);
                setError(
                    looksHtml
                        ? `Server returned HTML instead of JSON (wrong address or API down). Using ${API_BASE_URL}. Start the Nezoola API and set REACT_APP_PROXY_TARGET or REACT_APP_API_BASE_URL in .env, then restart npm start.`
                        : `Could not read server response. Using ${API_BASE_URL}. Is the API running? Restart npm start after changing .env.`
                );
                return;
            }

            if (response.ok) {
                const user = data.data?.user;
                if (!user || user.role !== 'admin') {
                    setError('Invalid email or password.');
                    return;
                }

                setSuccess('Login successful!');
                setStoredTokens(data.data.token, data.data.refreshToken);
                onLogin(user, data.data.token);
            } else {
                setError('Invalid email or password.');
            }
        } catch (error) {
            setError(
                `Network error — could not reach the API (${API_BASE_URL || 'via /api proxy'}). Start the Nezoola backend and set REACT_APP_PROXY_TARGET in .env to its URL (e.g. http://127.0.0.1:3001 for Docker), then restart npm start.`
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            {error && (
                <Alert
                    severity="error"
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                            color: 'error.main'
                        }
                    }}
                >
                    {error}
                </Alert>
            )}
            {success && (
                <Alert
                    severity="success"
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                            color: 'success.main'
                        }
                    }}
                >
                    {success}
                </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
                <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': {
                                borderColor: 'primary.main'
                            }
                        }
                    }}
                />

                <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': {
                                borderColor: 'primary.main'
                            }
                        }
                    }}
                />

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={<LoginIcon />}
                    sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        py: 1.5,
                        mb: 2,
                        '&:hover': {
                            background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)'
                        },
                        '&:disabled': {
                            background: 'rgba(0, 0, 0, 0.12)'
                        }
                    }}
                >
                    {loading ? 'Processing...' : 'Login'}
                </Button>
            </Box>
        </Box>
    );
};

export default LoginForm;
